import type { CSSProperties, ReactNode } from 'react';
import { memo, useMemo, useState, useLayoutEffect } from 'react';
import { cn } from '@/shared/utils/cn';
import type { GhanttTimelineSlot, GhanttUpperSegment } from './ganttTimeline';
import type { GhanttTimelineScale } from './types';
import { useGhanttConfig } from './GhanttConfigContext';
import { useGhanttScrollVirtual } from './GhanttScrollVirtualContext';

/** A partir de este número de slots se usa una rejilla CSS (una sola capa) en lugar de un nodo por columna. */
export const GHANTT_DENSE_SLOT_GRID_MIN_SLOTS = 96;

/**
 * Con escala `hour` / `day` / `week` y al menos tantos slots, la **cabecera** (etiquetas por slot)
 * virtualiza celdas con {@link ghanttScrollVirtualIndexRange}. El **cuerpo** con ancho fijo usa una
 * sola capa CSS (gradiente) para todos los días/semanas; no duplica esa lógica por fila.
 */
export const GHANTT_SCROLL_VIRTUAL_MIN_SLOTS = 120;

/** @deprecated Usar {@link GHANTT_SCROLL_VIRTUAL_MIN_SLOTS}. */
export const GHANTT_HOUR_VIRTUAL_MIN_SLOTS = GHANTT_SCROLL_VIRTUAL_MIN_SLOTS;

const GHANTT_SCROLL_VIRTUAL_ACTIVE_MIN = 200;
const GHANTT_SCROLL_VIRTUAL_ACTIVE_MAX = 300;

/**
 * Rango de índices de slots (misma anchura en px) a renderizar para scroll fluido:
 * ~200–300 slots “activos” anclados al scroll (viewport + colchón hacia ambos lados).
 */
export function ghanttScrollVirtualIndexRange(
  scrollLeftPx: number,
  viewportWidthPx: number,
  cellPx: number,
  slotCount: number,
): { start: number; end: number } {
  if (slotCount <= 0) return { start: 0, end: 0 };
  const cell = Math.max(1, Math.round(cellPx));
  const vw = Math.max(1, viewportWidthPx);
  const left = Math.max(0, scrollLeftPx);
  /** Primera columna que entra por la izquierda del viewport (ancla al scroll). */
  const first = Math.floor(left / cell);
  const lastVisible = Math.min(slotCount - 1, Math.floor((left + vw - 1) / cell));
  const viewSlots = Math.max(1, lastVisible - first + 1);
  let target = Math.min(
    GHANTT_SCROLL_VIRTUAL_ACTIVE_MAX,
    Math.max(GHANTT_SCROLL_VIRTUAL_ACTIVE_MIN, viewSlots + 96),
  );
  target = Math.min(target, slotCount);
  const extra = Math.max(0, target - viewSlots);
  const padBefore = Math.floor(extra / 2);
  let start = Math.max(0, first - padBefore);
  const end = Math.min(slotCount - 1, start + target - 1);
  if (end - start + 1 < target) {
    start = Math.max(0, end - target + 1);
  }
  return { start, end };
}

/** @deprecated Usar {@link ghanttScrollVirtualIndexRange}. */
export const ghanttHourVirtualIndexRange = ghanttScrollVirtualIndexRange;

/** Tope usado por {@link ghanttDenseHeaderLabelIndices} (utilidades de muestreo; la cabecera del strip pinta un label por slot). */
export const GHANTT_DENSE_HEADER_MAX_LABELS = 72;

/**
 * Índices de slot en los que dibujar etiqueta en cabecera densa (inicio + cada ~step + último slot).
 */
export function ghanttDenseHeaderLabelIndices(slotCount: number, maxLabels: number): number[] {
  if (slotCount <= 0) return [];
  const step = Math.max(1, Math.ceil(slotCount / maxLabels));
  const out: number[] = [];
  for (let i = 0; i < slotCount; i += step) {
    out.push(i);
  }
  if (out[out.length - 1] !== slotCount - 1) {
    out.push(slotCount - 1);
  }
  return out;
}

/**
 * Índices de etiquetas en cabecera densa repartidos **por cada bloque del piso superior** (p. ej. mes),
 * para que al hacer scroll siempre haya etiquetas secundarias en el viewport. Si `upperSpans` no
 * encaja con `totalSlots`, se usa {@link ghanttDenseHeaderLabelIndices} global.
 */
export function ghanttDenseHeaderLabelIndicesByUpperSpans(
  totalSlots: number,
  upperSpans: readonly number[],
  maxLabels: number,
): number[] {
  if (totalSlots <= 0) return [];
  const sum = upperSpans.reduce((a, b) => a + b, 0);
  if (upperSpans.length === 0 || sum !== totalSlots) {
    return ghanttDenseHeaderLabelIndices(totalSlots, maxLabels);
  }
  const perSegBudget = Math.max(2, Math.ceil(maxLabels / upperSpans.length));
  const set = new Set<number>();
  let start = 0;
  for (const span of upperSpans) {
    const cap = Math.min(span, perSegBudget);
    const localIx = ghanttDenseHeaderLabelIndices(span, cap);
    for (const li of localIx) {
      set.add(start + li);
    }
    start += span;
  }
  return Array.from(set).sort((a, b) => a - b);
}

export interface GhanttSlotStripProps {
  slots: readonly GhanttTimelineSlot[];
  /** Segmentos mayores (meses, años) para dibujar la rejilla principal. Si se pasan, se ocultan las líneas de los slots individuales. */
  upperSegments?: readonly GhanttUpperSegment[];
  /** `header`: fondo de cabecera; `body`: transparente para heredar fila (selección, hijos, zebra). */
  variant: 'header' | 'body';
  /** Contenido por celda de slot (cabecera con etiquetas). En cuerpo suele omitirse. */
  renderSlot?: (slot: GhanttTimelineSlot) => ReactNode;
  className?: string;
  /** Ancho fijo por slot (px). Suele ir dentro de un contenedor con `overflow-x-auto` en la columna Gantt. */
  slotMinWidthPx?: number;
  /**
   * Umbral para rejilla densa **solo en cuerpo** (gradiente repetido). La cabecera siempre usa una celda por slot.
   * @default {@link GHANTT_DENSE_SLOT_GRID_MIN_SLOTS}
   */
  densePatternMinSlots?: number;
}

function ghanttSlotStripPropsEqual(prev: GhanttSlotStripProps, next: GhanttSlotStripProps): boolean {
  if (prev.variant !== next.variant) return false;
  if (prev.slotMinWidthPx !== next.slotMinWidthPx) return false;
  if (prev.className !== next.className) return false;
  if (prev.renderSlot !== next.renderSlot) return false;
  if ((prev.densePatternMinSlots ?? GHANTT_DENSE_SLOT_GRID_MIN_SLOTS) !== (next.densePatternMinSlots ?? GHANTT_DENSE_SLOT_GRID_MIN_SLOTS)) {
    return false;
  }

  // Si el número de slots cambia, re-renderizar
  if (prev.slots.length !== next.slots.length) return false;
  
  // Comparación rápida de los extremos de los slots
  if (prev.slots.length > 0) {
    if (prev.slots[0].startMs !== next.slots[0].startMs) return false;
    const li = prev.slots.length - 1;
    if (prev.slots[li].startMs !== next.slots[li].startMs) return false;
  }

  // Comparación de upperSegments (evitamos el re-render por nueva instancia de array si el contenido es idéntico)
  const pSeg = prev.upperSegments || [];
  const nSeg = next.upperSegments || [];
  if (pSeg.length !== nSeg.length) return false;
  for (let i = 0; i < pSeg.length; i++) {
    if (pSeg[i].slotSpan !== nSeg[i].slotSpan || pSeg[i].label !== nSeg[i].label) {
      return false;
    }
  }

  return true;
}

/** Ancho de celda en px alineado a píxeles enteros (cabecera, cuerpo y gradiente comparten el mismo valor). */
export function ghanttSnapSlotWidthPx(slotMinWidthPx: number): number {
  return Math.max(2, Math.round(slotMinWidthPx));
}

/**
 * Líneas verticales entre slots o segmentos mayores.
 * `slotPixelWidth` suele ser {@link ghanttSnapSlotWidthPx}(`columnWidthPx`).
 */
export function ghanttVerticalSlotGridBackgroundStyle(
  slotCount: number,
  slotPixelWidth: number,
  upperSegments?: readonly GhanttUpperSegment[],
  hideMinorGrid?: boolean,
): CSSProperties {
  const cell = ghanttSnapSlotWidthPx(slotPixelWidth);
  const w = slotCount * cell;

  // Colores: uno suave para los slots (días/semanas) y uno más fuerte para los meses/años.
  const minorColor = 'var(--ui-color-border-subtle, #f8fafc)';
  const majorColor = 'var(--ui-color-border-default, #a1a1aa)';

  /**
   * REJILLA MENOR (Slots)
   * En lugar de un repeating-gradient de ancho infinito, usamos un patrón de 1 celda
   * y dejamos que el navegador lo repita con background-size.
   */
  const minorBg = hideMinorGrid
    ? 'none'
    : `linear-gradient(to right, transparent 0%, transparent calc(100% - 1px), ${minorColor} calc(100% - 1px), ${minorColor} 100%)`;

  /**
   * REJILLA MAYOR (Segmentos)
   * Usamos porcentajes para que el string sea estable durante el zoom.
   */
  let majorBg = 'none';
  if (upperSegments && upperSegments.length > 0) {
    const stops: string[] = [];
    let currentSlots = 0;
    for (const seg of upperSegments) {
      currentSlots += seg.slotSpan;
      if (currentSlots >= slotCount) break;
      const pct = (currentSlots / slotCount) * 100;
      // Una línea de 1px a la derecha de cada segmento
      stops.push(`transparent calc(${pct}% - 1px), ${majorColor} calc(${pct}% - 1px), ${majorColor} ${pct}%, transparent ${pct}%`);
    }
    if (stops.length > 0) {
      majorBg = `linear-gradient(to right, transparent 0%, ${stops.join(', ')})`;
    }
  }

  return {
    width: w,
    minWidth: w,
    minHeight: '100%',
    backgroundImage: majorBg !== 'none' ? `${majorBg}, ${minorBg}` : minorBg,
    backgroundSize: majorBg !== 'none' ? `100% 100%, ${cell}px 100%` : `${cell}px 100%`,
    backgroundRepeat: 'no-repeat, repeat-x',
  };
}

/**
 * Cuerpo con ancho fijo por slot: una sola capa de rejilla (sin virtualización ni contexto de scroll).
 * Así las N filas no se re-renderizan al mover el timeline.
 */
function GhanttSlotStripBodyDense({
  slots,
  upperSegments,
  className,
  slotMinWidthPx,
}: GhanttSlotStripProps) {
  const px = slotMinWidthPx!;
  const style = useMemo(
    () => ghanttVerticalSlotGridBackgroundStyle(slots.length, px, upperSegments),
    [slots.length, px, upperSegments],
  );
  return (
    <div
      className={cn(
        'ui-flex ui-h-full ui-min-h-0 ui-min-w-0 ui-w-full',
        'ui-bg-transparent',
        className,
      )}
      role="presentation"
      aria-hidden
    >
      <div className="ui-h-full ui-shrink-0" style={style} />
    </div>
  );
}

/**
 * Rejilla de líneas verticales: con `slotMinWidthPx`, ancho fijo por día y contenedor padre con scroll;
 * sin ello, **flex 1 1 0** reparte el ancho visible entre slots.
 */
function GhanttSlotStripInner({
  slots,
  upperSegments,
  variant,
  renderSlot,
  className,
  slotMinWidthPx,
  densePatternMinSlots = GHANTT_DENSE_SLOT_GRID_MIN_SLOTS,
}: GhanttSlotStripProps) {
  const cfg = useGhanttConfig();
  const sv = useGhanttScrollVirtual();
  const hs = cfg?.horizontalScroll;
  const fixedSlots = slotMinWidthPx != null && slotMinWidthPx > 0;
  const cell = fixedSlots ? ghanttSnapSlotWidthPx(slotMinWidthPx!) : 0;

  const scale = (cfg?.timeline.scale ?? 'day') as GhanttTimelineScale;
  const scrollVirtualLong =
    (scale === 'hour' || scale === 'day' || scale === 'week') &&
    slots.length >= GHANTT_SCROLL_VIRTUAL_MIN_SLOTS;

  const useProviderVirtualRange = sv.managed && fixedSlots && scrollVirtualLong;

  const [scrollVirtualRange, setScrollVirtualRange] = useState<{ start: number; end: number } | null>(
    null,
  );

  const fallbackProviderRange = useMemo(
    () =>
      ghanttScrollVirtualIndexRange(
        0,
        typeof globalThis !== 'undefined' && 'innerWidth' in globalThis
          ? Math.max(1, (globalThis as unknown as { innerWidth: number }).innerWidth)
          : 1024,
        cell,
        slots.length,
      ),
    [cell, slots.length],
  );

  /** Cabecera + cuerpo (rejilla no densa): ~200–300 slots activos (hora/día/semana) siguiendo el scroll. */
  useLayoutEffect(() => {
    if (useProviderVirtualRange) {
      setScrollVirtualRange((prev) => (prev == null ? prev : null));
      return;
    }
    if (!fixedSlots || !scrollVirtualLong || !hs?.headerViewportRef) {
      setScrollVirtualRange((prev) => (prev == null ? prev : null));
      return;
    }

    let cancelled = false;
    let attachRaf = 0;
    let scrollRaf = 0;
    let detachScroll: (() => void) | undefined;

    const tick = (el: HTMLElement) => {
      const r = ghanttScrollVirtualIndexRange(el.scrollLeft, el.clientWidth, cell, slots.length);
      setScrollVirtualRange((prev) =>
        prev && prev.start === r.start && prev.end === r.end ? prev : r,
      );
    };

    let tries = 0;
    const tryAttach = () => {
      const el = hs.headerViewportRef.current;
      if (!el) {
        if (!cancelled && tries++ < 32) {
          attachRaf = requestAnimationFrame(tryAttach);
        }
        return;
      }
      const onScroll = () => {
        if (scrollRaf !== 0) return;
        scrollRaf = requestAnimationFrame(() => {
          scrollRaf = 0;
          if (!cancelled) tick(el);
        });
      };
      tick(el);
      const onResize = () => tick(el);
      el.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onResize);
      detachScroll = () => {
        el.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
      };
    };
    tryAttach();

    return () => {
      cancelled = true;
      if (attachRaf !== 0) cancelAnimationFrame(attachRaf);
      if (scrollRaf !== 0) cancelAnimationFrame(scrollRaf);
      detachScroll?.();
    };
  }, [useProviderVirtualRange, fixedSlots, scrollVirtualLong, hs?.headerViewportRef, cell, slots.length]);

  /** Si hay demasiados slots (sin ventana virtual hora/día/semana), muestreamos cabecera densa. */
  const isDenseHeader =
    variant === 'header' &&
    !scrollVirtualLong &&
    slots.length > (densePatternMinSlots || GHANTT_DENSE_SLOT_GRID_MIN_SLOTS) &&
    (slotMinWidthPx ?? 0) < 36;

  const labelIndices = useMemo(() => {
    if (!isDenseHeader) return null;
    const maxL = 120; // Aumentamos un poco el presupuesto de etiquetas
    if (upperSegments && upperSegments.length > 0) {
      return new Set(
        ghanttDenseHeaderLabelIndicesByUpperSpans(
          slots.length,
          upperSegments.map((s) => s.slotSpan),
          maxL,
        ),
      );
    }
    return new Set(ghanttDenseHeaderLabelIndices(slots.length, maxL));
  }, [isDenseHeader, slots.length, upperSegments]);

  const resolvedVirtualRange = useMemo(() => {
    if (!fixedSlots || !scrollVirtualLong) return null;
    if (useProviderVirtualRange) {
      return sv.slotRange ?? fallbackProviderRange;
    }
    return scrollVirtualRange;
  }, [
    fixedSlots,
    scrollVirtualLong,
    useProviderVirtualRange,
    sv.slotRange,
    fallbackProviderRange,
    scrollVirtualRange,
  ]);

  const indices = useMemo(() => {
    if (labelIndices) return Array.from(labelIndices);
    if (fixedSlots && scrollVirtualLong && resolvedVirtualRange) {
      const out: number[] = [];
      for (let i = resolvedVirtualRange.start; i <= resolvedVirtualRange.end; i++) out.push(i);
      return out;
    }
    return slots.map((_, i) => i);
  }, [labelIndices, fixedSlots, scrollVirtualLong, resolvedVirtualRange, slots.length]);

  const fixedSlotGridBackgroundStyle = useMemo(() => {
    if (!fixedSlots || slots.length === 0) return undefined;
    return ghanttVerticalSlotGridBackgroundStyle(slots.length, cell, upperSegments);
  }, [fixedSlots, slots.length, cell, upperSegments]);

  /** Con ancho fijo por slot: rejilla por gradiente (alineada con piso superior), una celda por slot en cabecera. */
  if (fixedSlots && slots.length > 0) {
    const totalW = slots.length * cell;

    return (
      <div
        className={cn(
          'ui-relative ui-box-border ui-flex ui-h-full ui-min-h-0 ui-w-full ui-min-w-0 ui-shrink-0',
          variant === 'header' && 'ui-bg-transparent',
          variant === 'body' && 'ui-bg-transparent',
          className,
        )}
        role="presentation"
      >
        <div className="ui-pointer-events-none ui-absolute ui-inset-0 ui-overflow-hidden" aria-hidden>
          <div
            className="ui-h-full ui-min-h-0 ui-shrink-0"
            style={fixedSlotGridBackgroundStyle}
          />
        </div>
        <div
          className={cn(
            'ui-relative ui-z-[1] ui-box-border ui-h-full ui-min-h-0 ui-shrink-0',
          )}
          style={{ width: totalW, minWidth: totalW }}
        >
          {indices.map((idx) => {
            const slot = slots[idx];
            if (!slot) return null;
            return (
              <div
                key={slot.startMs}
                className={cn(
                  'ui-absolute ui-top-0 ui-box-border ui-flex ui-h-full ui-min-h-0 ui-items-center ui-justify-center ui-px-0.5 ui-py-1',
                  'ui-truncate ui-overflow-hidden',
                )}
                style={{ left: idx * cell, width: cell }}
              >
                {renderSlot ? renderSlot(slot) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'ui-flex ui-h-full ui-min-h-0 ui-min-w-0 ui-w-full',
        variant === 'header' && 'ui-bg-transparent',
        variant === 'body' && 'ui-bg-transparent',
        className,
      )}
      role="presentation"
    >
      {slots.map((slot, index) => (
        <div
          key={slot.startMs}
          className={cn(
            'ui-box-border ui-flex ui-h-full ui-min-h-0',
            /** Sin ancho fijo: reparto flexible; línea solo entre columnas. */
            index > 0 && 'ui-border-l ui-border-l-default',
            'ui-min-w-0 ui-flex-1',
            renderSlot && 'ui-items-center ui-justify-center ui-px-0 ui-py-1',
          )}
        >
          {renderSlot ? renderSlot(slot) : null}
        </div>
      ))}
    </div>
  );
}

function GhanttSlotStripRoot(props: GhanttSlotStripProps) {
  const fixed = props.slotMinWidthPx != null && props.slotMinWidthPx > 0;
  if (props.variant === 'body' && fixed) {
    return <GhanttSlotStripBodyDense {...props} />;
  }
  return <GhanttSlotStripInner {...props} />;
}

export const GhanttSlotStrip = memo(GhanttSlotStripRoot, ghanttSlotStripPropsEqual);
GhanttSlotStrip.displayName = 'GhanttSlotStrip';
